import React, {useState} from "react";
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogContentText,
    DialogActions,
    Button,
    Typography,
    TextField,
    Stepper,
    Step,
    StepLabel,
    Box,
    Paper,
    Divider,
    IconButton,
    Slide,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Card,
    CardContent,
    CardActions,
    Grid,
    Chip
} from "@mui/material";
import {LoadingButton} from "@mui/lab";
import {styled} from '@mui/material/styles';
import CloseIcon from "@mui/icons-material/Close";
import SendIcon from "@mui/icons-material/Send";
import WarningIcon from "@mui/icons-material/Warning";
import DeleteIcon from "@mui/icons-material/Delete";
import DownloadIcon from "@mui/icons-material/Download";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import InfoIcon from "@mui/icons-material/Info";
import FastForwardIcon from "@mui/icons-material/FastForward";
import GetAppIcon from "@mui/icons-material/GetApp";
import DescriptionIcon from "@mui/icons-material/Description";
import { handle401Unauthorized, handle403Forbidden } from '../../../utils/authErrorHandler';
import { getUserData } from '../../../services/api/client';


// Transition for dialogs
const Transition = React.forwardRef(function Transition(props, ref) {
    return <Slide direction="up" ref={ref} {...props} />;
});

// Styled components
const StyledDialog = styled(Dialog)(({theme}) => ({
    '& .MuiDialog-paper': {
        borderRadius: 12,
        boxShadow: '0 10px 25px rgba(0,0,0,0.1)',
    },
}));

const StyledDialogTitle = styled(DialogTitle)(({theme}) => ({
    backgroundColor: theme.palette.background.default,
    padding: theme.spacing(3),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
}));

const DeleteDialogContent = styled(DialogContent)(({theme}) => ({
    padding: theme.spacing(4),
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    textAlign: 'center',
}));

// Property Delete Dialog Component
export const PropertyDeleteDialog = ({open, propertyName, onClose, onConfirm}) => {
    return (
        <StyledDialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={onClose}
            aria-describedby="alert-dialog-delete-description"
            maxWidth="sm"
            fullWidth
        >
            <StyledDialogTitle>
                <Typography variant="h6" component="div" sx={{display: 'flex', alignItems: 'center'}}>
                    <DeleteIcon color="error" sx={{mr: 1}}/>
                    Delete Property
                </Typography>
                <IconButton edge="end" color="inherit" onClick={onClose} aria-label="close">
                    <CloseIcon/>
                </IconButton>
            </StyledDialogTitle>

            <DeleteDialogContent>
                <WarningIcon color="error" sx={{fontSize: 60, mb: 2}}/>
                <Typography variant="h6" gutterBottom>
                    You are about to delete "{propertyName}"
                </Typography>
                <Typography variant="body1" id="alert-dialog-delete-description" color="text.secondary" sx={{mb: 3}}>
                    Deleting a property will permanently remove it from your portfolio, including all its units and
                    tenant information. This action cannot be undone.
                </Typography>
            </DeleteDialogContent>

            <DialogActions sx={{padding: 3, borderTop: '1px solid', borderColor: 'divider'}}>
                <Button
                    onClick={onClose}
                    variant="outlined"
                    color="primary"
                    sx={{borderRadius: 2}}
                >
                    Cancel
                </Button>
                <Button
                    onClick={onConfirm}
                    variant="contained"
                    color="error"
                    sx={{borderRadius: 2}}
                >
                    Delete Property
                </Button>
            </DialogActions>
        </StyledDialog>
    );
};

// Property Add Dialog Component
export const PropertyAddDialog = ({
                                      open,
                                      onClose,
                                      onPropertyCreated,
                                      landlordId,
                                      emailAddress,
                                      apiDetails,
                                      showSnackbar
                                  }) => {
    // Define steps
    const steps = [
        "Choose Method",
        "Property Details",
        "Download Template",
        "Fill Template",
        "Upload File",
    ];

    // State
    const [activeStep, setActiveStep] = useState(0);
    const [propertyName, setPropertyName] = useState("");
    const [propertyFile, setPropertyFile] = useState(null);
    const [templateFile, setTemplateFile] = useState("");
    const [isLoading, setIsLoading] = useState(false);
    const [hasDownloaded, setHasDownloaded] = useState(false);
    const [hasExistingFile, setHasExistingFile] = useState(false);
    const [uploadResults, setUploadResults] = useState(null);

    // Handle next step
    const handleNext = () => {
        setActiveStep((prevStep) => prevStep + 1);
    };

    // Handle back step
    const handleBack = () => {
        setActiveStep((prevStep) => prevStep - 1);
    };

    // Handle property name change
    const handlePropertyNameChange = (e) => {
        setPropertyName(e.target.value);
    };

    // Handle file change
    const handleFileChange = (e) => {
        if (e.target.files && e.target.files[0]) {
            setPropertyFile(e.target.files[0]);
        }
    };

    // Reset dialog state
    const handleCloseDialog = () => {
        setActiveStep(0);
        setPropertyName("");
        setPropertyFile(null);
        setTemplateFile("");
        setHasDownloaded(false);
        setHasExistingFile(false);
        onClose();
    };

    // Handle method selection
    const handleMethodSelection = (hasFile) => {
        setHasExistingFile(hasFile);
        if (hasFile) {
            // Skip directly to upload step
            setActiveStep(4);
        } else {
            // Go to normal flow
            setActiveStep(1);
        }
    };

    // Convert base64 to blob
    const base64ToBlob = (base64) => {
        const binaryString = window.atob(base64);
        const bytes = new Uint8Array(binaryString.length);

        for (let i = 0; i < binaryString.length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return new Blob([bytes], {
            type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
        });
    };

    // Get template
    const getTemplate = async () => {
        if (!propertyName.trim()) {
            showSnackbar({
                text: "Property name cannot be blank",
                type: "error",
            });
            return;
        }

        setIsLoading(true);

        // Get auth token from localStorage
        const userData = getUserData();
        const authToken = userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;

        try {
            const response = await fetch(`${apiDetails}pegasus/visionary/files/download`, {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    "token": authToken || ""
                },
                body: JSON.stringify({
                    email_address: emailAddress,
                    Property_Name: propertyName,
                }),
            });

            const data = await response.json();

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                setTemplateFile(data["File"]);
                setIsLoading(false);
            } else {
                showSnackbar({
                    text: "Failed to get template",
                    type: "error",
                });
                setIsLoading(false);
            }
        } catch (error) {
            console.error(error);
            showSnackbar({
                text: "Connection error",
                type: "error",
            });
            setIsLoading(false);
        }
    };

    // Download template
    const downloadTemplate = () => {
        if (!templateFile) {
            showSnackbar({
                text: "Template not available",
                type: "error",
            });
            return;
        }

        setIsLoading(true);

        try {
            const blob = base64ToBlob(templateFile);
            const blobURL = URL.createObjectURL(blob);
            const link = document.createElement("a");

            link.setAttribute("download", `${propertyName}_Template.xlsx`);
            link.href = blobURL;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);

            setHasDownloaded(true);
            setIsLoading(false);
        } catch (error) {
            console.error(error);
            showSnackbar({
                text: "Download failed",
                type: "error",
            });
            setIsLoading(false);
        }
    };

// Enhanced upload function for FinishSetupV2 with feedback Excel download
    const uploadFilledTemplate = async () => {
        if (!propertyFile) {
            showSnackbar({
                text: "Please select a file to upload",
                type: "error",
            });
            return;
        }

        setIsLoading(true);

        const formData = new FormData();
        formData.append(
            "details",
            JSON.stringify({
                landlord_id: landlordId,
                email_address: emailAddress,
            })
        );
        formData.append("updated_document", propertyFile);

        try {
            // Get auth token from localStorage
            const userData = getUserData();
            const authToken = userData ? userData.find(([key]) => key === 'access_token')?.[1] : null;

            // Update to use the new FinishSetupV2 endpoint
            const response = await fetch(`${apiDetails}pegasus/visionary/files/finishSetup`, {
                method: "POST",
                headers: {
                    "token": authToken || ""
                },
                body: formData,
            });

            const data = await response.json();

             // Handle authentication/authorization errors
                if (response.status === 401) {
                    handle401Unauthorized();
                    return;
                }
                if (response.status === 403) {
                    handle403Forbidden();
                    return;
                }
            if (response.status === 200) {
                // Handle successful upload with detailed feedback
                handleSuccessfulUpload(data);
            } else {
                // Handle errors
                handleUploadError(data, response.status);
            }
        } catch (error) {
            console.error("Upload error:", error);
            showSnackbar({
                text: "Upload failed. Please check your connection and try again.",
                type: "error",
            });
            setIsLoading(false);
        }
    };

// Handle successful upload response
    const handleSuccessfulUpload = (data) => {
        const {
            property_id,
            total_tenants,
            total_units,
            failed_tenants,
            vacant_units,
            feedback_excel,
            feedback_filename
        } = data;

        // Show success message with details
        const successMessage = `Property created successfully! 
        • ${total_tenants} tenants created
        • ${total_units} units processed
        ${failed_tenants > 0 ? `• ${failed_tenants} records failed` : ''}
        ${vacant_units > 0 ? `• ${vacant_units} vacant units` : ''}`;

        showSnackbar({
            text: successMessage,
            type: "success",
            duration: 6000, // Show longer for detailed message
        });

        // Download feedback Excel if available
        if (feedback_excel && feedback_filename) {
            downloadFeedbackExcel(feedback_excel, feedback_filename);

            // Show additional message about feedback file
            setTimeout(() => {
                showSnackbar({
                    text: "📋 Detailed upload results have been downloaded for your review",
                    type: "info",
                    duration: 5000,
                });
            }, 1000);
        }

        // Store results for potential later use
        setUploadResults({
            propertyId: property_id,
            totalTenants: total_tenants,
            totalUnits: total_units,
            failedTenants: failed_tenants,
            vacantUnits: vacant_units,
            hasFailures: failed_tenants > 0
        });

        setIsLoading(false);
        handleNext(); // Move to success step
        onPropertyCreated(); // Call the callback
    };

// Handle upload errors
    const handleUploadError = (data, statusCode) => {
        let errorMessage = "Something went wrong. Please try again later";

        // Handle specific error cases
        if (statusCode === 400) {
            if (data.Message && data.Message.includes("properties_property_name_landlord_id_uindex")) {
                errorMessage = "Property with this name already exists";
            } else if (data.Message && data.Message.includes("Invalid Excel format")) {
                errorMessage = "Invalid Excel format. Please use the provided template";
            } else if (data.Message && data.Message.includes("Missing required columns")) {
                errorMessage = "Excel file is missing required columns. Please check your template";
            } else if (data.Message) {
                errorMessage = data.Message;
            }
        } else if (statusCode === 413) {
            errorMessage = "File is too large. Please reduce file size and try again";
        } else if (statusCode === 500) {
            errorMessage = "Server error occurred. Please try again or contact support";
        }

        showSnackbar({
            text: errorMessage,
            type: "error",
            duration: 5000,
        });

        setIsLoading(false);
    };

// Download feedback Excel file
    const downloadFeedbackExcel = (base64Excel, filename) => {
        try {
            // Convert base64 to blob
            const binaryString = window.atob(base64Excel);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }

            const blob = new Blob([bytes], {
                type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet'
            });

            // Create download link
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            link.download = filename || 'Upload_Results.xlsx';

            // Trigger download
            document.body.appendChild(link);
            link.click();

            // Cleanup
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);

        } catch (error) {
            console.error('Error downloading feedback Excel:', error);
            showSnackbar({
                text: "Could not download feedback file, but upload was successful",
                type: "warning",
            });
        }
    };

// Add this state variable to your component (where you already have other state)
// const [uploadResults, setUploadResults] = useState(null);

// Enhanced success step component to show detailed results
    const SuccessStep = ({ uploadResults }) => {
        return (
            <div className="success-step">
                <div className="success-header">
                    {/* Use your existing success icon component or replace with any icon */}
                    <div className="success-icon">✅</div>
                    <h2>Property Created Successfully!</h2>
                </div>

                {uploadResults && (
                    <div className="upload-summary">
                        <h3>Upload Summary</h3>
                        <div className="summary-grid">
                            <div className="summary-item">
                                <span className="label">Total Units:</span>
                                <span className="value">{uploadResults.totalUnits}</span>
                            </div>
                            <div className="summary-item">
                                <span className="label">Tenants Created:</span>
                                <span className="value success">{uploadResults.totalTenants}</span>
                            </div>
                            {uploadResults.vacantUnits > 0 && (
                                <div className="summary-item">
                                    <span className="label">Vacant Units:</span>
                                    <span className="value neutral">{uploadResults.vacantUnits}</span>
                                </div>
                            )}
                            {uploadResults.failedTenants > 0 && (
                                <div className="summary-item">
                                    <span className="label">Failed Records:</span>
                                    <span className="value error">{uploadResults.failedTenants}</span>
                                </div>
                            )}
                        </div>

                        {uploadResults.hasFailures && (
                            <div className="failure-notice">
                                <span>⚠️</span>
                                <p>
                                    Some records could not be processed. Check the downloaded
                                    feedback file for details and specific reasons.
                                </p>
                            </div>
                        )}
                    </div>
                )}

                <div className="next-steps">
                    <h3>Next Steps</h3>
                    <ul>
                        <li>Review the feedback Excel file for any failed records</li>
                        <li>Fix any issues and upload a new file with only the failed records</li>
                        <li>Your property is now ready for tenant management</li>
                    </ul>
                </div>
            </div>
        );
    };

// CSS styles for the success step
    const styles = `
.success-step {
    text-align: center;
    padding: 2rem;
}

.success-header {
    margin-bottom: 2rem;
}

.success-icon {
    font-size: 4rem;
    color: #4caf50;
    margin-bottom: 1rem;
}

.upload-summary {
    background: #f8f9fa;
    border-radius: 8px;
    padding: 1.5rem;
    margin: 2rem 0;
    text-align: left;
}

.summary-grid {
    display: grid;
    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
    gap: 1rem;
    margin: 1rem 0;
}

.summary-item {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem;
    background: white;
    border-radius: 4px;
}

.value.success { color: #4caf50; font-weight: bold; }
.value.error { color: #f44336; font-weight: bold; }
.value.neutral { color: #ff9800; font-weight: bold; }

.failure-notice {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    background: #fff3cd;
    border: 1px solid #ffeaa7;
    border-radius: 4px;
    padding: 1rem;
    margin-top: 1rem;
    color: #856404;
}

.next-steps {
    text-align: left;
    margin-top: 2rem;
}

.next-steps ul {
    padding-left: 1.5rem;
}

.next-steps li {
    margin-bottom: 0.5rem;
}
`;

    // Render step content
    const getStepContent = (step) => {
        switch (step) {
            case 0:
                return (
                    <Box sx={{p: 2}}>
                        <Typography variant="h6" gutterBottom align="center" sx={{mb: 3}}>
                            How would you like to add your property?
                        </Typography>

                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Card
                                    elevation={2}
                                    sx={{
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            elevation: 4,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => handleMethodSelection(false)}
                                >
                                    <CardContent sx={{textAlign: 'center', p: 3}}>
                                        <GetAppIcon
                                            color="primary"
                                            sx={{fontSize: 48, mb: 2}}
                                        />
                                        <Typography variant="h6" gutterBottom>
                                            Download Template
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            I need to download and fill out the property template
                                        </Typography>
                                        <Chip
                                            label="Guided Process"
                                            color="primary"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </CardContent>
                                    <CardActions sx={{justifyContent: 'center', pb: 3}}>
                                        <Button
                                            variant="outlined"
                                            color="primary"
                                            startIcon={<DownloadIcon/>}
                                        >
                                            Start Here
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>

                            <Grid item xs={12} md={6}>
                                <Card
                                    elevation={2}
                                    sx={{
                                        height: '100%',
                                        cursor: 'pointer',
                                        transition: 'all 0.2s',
                                        '&:hover': {
                                            elevation: 4,
                                            transform: 'translateY(-2px)'
                                        }
                                    }}
                                    onClick={() => handleMethodSelection(true)}
                                >
                                    <CardContent sx={{textAlign: 'center', p: 3}}>
                                        <DescriptionIcon
                                            color="success"
                                            sx={{fontSize: 48, mb: 2}}
                                        />
                                        <Typography variant="h6" gutterBottom>
                                            Upload Existing File
                                        </Typography>
                                        <Typography variant="body2" color="text.secondary" paragraph>
                                            I already have a completed Excel file ready to upload
                                        </Typography>
                                        <Chip
                                            label="Quick Upload"
                                            color="success"
                                            variant="outlined"
                                            size="small"
                                        />
                                    </CardContent>
                                    <CardActions sx={{justifyContent: 'center', pb: 3}}>
                                        <Button
                                            variant="outlined"
                                            color="success"
                                            startIcon={<FastForwardIcon/>}
                                        >
                                            Skip to Upload
                                        </Button>
                                    </CardActions>
                                </Card>
                            </Grid>
                        </Grid>

                        <Paper
                            variant="outlined"
                            sx={{
                                mt: 3,
                                p: 2,
                                bgcolor: 'background.default',
                                border: '1px solid',
                                borderColor: 'divider'
                            }}
                        >
                            <Typography variant="caption" color="text.secondary"
                                        sx={{display: 'flex', alignItems: 'center'}}>
                                <InfoIcon fontSize="small" sx={{mr: 1}}/>
                                If you're unsure about the format, choose "Download Template" for a guided setup
                                process.
                            </Typography>
                        </Paper>
                    </Box>
                );
            case 1:
                return (
                    <Box sx={{p: 1}}>
                        <Typography variant="body1" gutterBottom>
                            Enter a name for your new property:
                        </Typography>
                        <TextField
                            required
                            id="propertyName"
                            label="Property Name"
                            fullWidth
                            variant="outlined"
                            value={propertyName}
                            onChange={handlePropertyNameChange}
                            margin="normal"
                        />
                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                            This name will be used to identify your property in the system.
                        </Typography>
                    </Box>
                );
            case 2:
                return (
                    <Box sx={{p: 1}}>
                        <Typography variant="body1" gutterBottom>
                            Download the property template Excel file:
                        </Typography>

                        <Paper variant="outlined" sx={{p: 2, mb: 2, bgcolor: '#f8f9fa'}}>
                            <Typography variant="body2" paragraph>
                                This template allows you to:
                            </Typography>
                            <List dense disablePadding>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <CheckCircleIcon fontSize="small" color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Define all units in your property"/>
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <CheckCircleIcon fontSize="small" color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Set initial meter readings for each unit"/>
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <CheckCircleIcon fontSize="small" color="primary"/>
                                    </ListItemIcon>
                                    <ListItemText primary="Specify vacant/occupied status of units"/>
                                </ListItem>
                            </List>
                        </Paper>

                        <Typography variant="body2" color="text.secondary" paragraph>
                            The first sheet of the Excel file provides an example of how to fill in the data. Please
                            follow the format closely to ensure successful property creation.
                        </Typography>

                        {templateFile ? (
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon/>}
                                onClick={downloadTemplate}
                                loading={isLoading}
                                fullWidth
                                sx={{mt: 2}}
                            >
                                Download Template
                            </LoadingButton>
                        ) : (
                            <LoadingButton
                                variant="contained"
                                color="primary"
                                startIcon={<DownloadIcon/>}
                                onClick={getTemplate}
                                loading={isLoading}
                                fullWidth
                                sx={{mt: 2}}
                            >
                                Generate Template
                            </LoadingButton>
                        )}
                    </Box>
                );
            case 3:
                return (
                    <Box sx={{p: 1}}>
                        <Typography variant="body1" gutterBottom>
                            Fill in the template with your property information:
                        </Typography>

                        <Paper variant="outlined" sx={{p: 2, mb: 2}}>
                            <Typography variant="subtitle2" color="primary" gutterBottom>
                                Important Instructions:
                            </Typography>
                            <Typography variant="body2" paragraph>
                                For each unit in your property, make sure to provide:
                            </Typography>
                            <List dense disablePadding>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <InfoIcon fontSize="small" color="info"/>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Unit number/name"
                                        secondary="A unique identifier for each unit"
                                    />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <InfoIcon fontSize="small" color="info"/>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Water meter reading"
                                        secondary="Enter 0 if not applicable"
                                    />
                                </ListItem>
                                <ListItem disableGutters>
                                    <ListItemIcon sx={{minWidth: 36}}>
                                        <InfoIcon fontSize="small" color="info"/>
                                    </ListItemIcon>
                                    <ListItemText
                                        primary="Electricity meter reading"
                                        secondary="Enter 0 if not applicable"
                                    />
                                </ListItem>
                            </List>
                        </Paper>

                        <Typography variant="body2" color="text.secondary" sx={{fontStyle: 'italic'}}>
                            Complete these details even for vacant units to ensure your property is set up correctly.
                        </Typography>
                    </Box>
                );
            case 4:
                return (
                    <Box sx={{p: 1}}>
                        <Typography variant="body1" gutterBottom>
                            {hasExistingFile ? 'Upload your Excel file:' : 'Upload your completed property template:'}
                        </Typography>

                        {hasExistingFile && (
                            <Paper variant="outlined" sx={{p: 2, mb: 2, bgcolor: '#e8f5e8'}}>
                                <Typography variant="body2" color="success.dark" gutterBottom>
                                    <CheckCircleIcon fontSize="small" sx={{mr: 1, verticalAlign: 'middle'}}/>
                                    Great! You can upload your existing Excel file directly.
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                    Make sure your file contains all necessary unit information including meter
                                    readings.
                                </Typography>
                            </Paper>
                        )}

                        <Paper
                            variant="outlined"
                            sx={{
                                p: 3,
                                mb: 2,
                                display: 'flex',
                                flexDirection: 'column',
                                alignItems: 'center',
                                border: '1px dashed',
                                borderColor: hasExistingFile ? 'success.main' : 'primary.main',
                                bgcolor: 'background.default'
                            }}
                        >
                            <UploadFileIcon
                                color={hasExistingFile ? 'success' : 'primary'}
                                sx={{fontSize: 40, mb: 2}}
                            />

                            {propertyFile ? (
                                <Box sx={{textAlign: 'center', width: '100%'}}>
                                    <Typography variant="subtitle2" color="primary" gutterBottom>
                                        File selected:
                                    </Typography>
                                    <Paper variant="outlined" sx={{
                                        p: 1,
                                        display: 'flex',
                                        justifyContent: 'space-between',
                                        alignItems: 'center'
                                    }}>
                                        <Typography variant="body2" noWrap sx={{maxWidth: '80%'}}>
                                            {propertyFile.name}
                                        </Typography>
                                        <IconButton size="small" color="error" onClick={() => setPropertyFile(null)}>
                                            <DeleteIcon fontSize="small"/>
                                        </IconButton>
                                    </Paper>
                                </Box>
                            ) : (
                                <>
                                    <Typography variant="body2" color="text.secondary" gutterBottom align="center">
                                        {hasExistingFile
                                            ? 'Select your Excel file with property and unit information'
                                            : 'Select the Excel file you downloaded and filled with your property details'
                                        }
                                    </Typography>
                                    <Button
                                        component="label"
                                        variant="outlined"
                                        startIcon={<UploadFileIcon/>}
                                        color={hasExistingFile ? 'success' : 'primary'}
                                        sx={{mt: 1}}
                                    >
                                        Choose File
                                        <input
                                            type="file"
                                            hidden
                                            accept=".xlsx,.xls"
                                            onChange={handleFileChange}
                                        />
                                    </Button>
                                </>
                            )}
                        </Paper>

                        <Typography variant="caption" color="text.secondary" sx={{display: 'block', mt: 1}}>
                            Only Excel files (.xlsx, .xls) are accepted. Make sure you've filled in all required
                            information.
                        </Typography>
                    </Box>
                );
            case 5:
                return (
                    <Box sx={{p: 2, textAlign: 'center'}}>
                        <SuccessStep uploadResults={uploadResults} />
                        <CheckCircleIcon color="success" sx={{fontSize: 60, mb: 2}}/>
                        <Typography variant="h6" gutterBottom>
                            Property Added Successfully!
                        </Typography>
                        <Typography variant="body1" color="text.secondary">
                            Your new property has been created and is now ready to manage.
                        </Typography>
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleCloseDialog}
                            sx={{mt: 3}}
                        >
                            Done
                        </Button>
                    </Box>
                );
            default:
                return "Unknown step";
        }
    };

    // Determine if next button should be enabled
    const isNextButtonEnabled = () => {
        switch (activeStep) {
            case 0:
                return false; // Method selection handles navigation directly
            case 1:
                return propertyName.trim() !== "";
            case 2:
                return hasDownloaded;
            case 3:
                return true;
            case 4:
                return !!propertyFile;
            default:
                return false;
        }
    };

    // Handle next button click
    const handleNextButtonClick = () => {
        switch (activeStep) {
            case 1:
                // First get the template if it doesn't exist
                if (!templateFile) {
                    getTemplate();
                } else {
                    handleNext();
                }
                break;
            case 2:
                // Download template if not downloaded
                if (!hasDownloaded) {
                    downloadTemplate();
                } else {
                    handleNext();
                }
                break;
            case 3:
                // Just move to next step
                handleNext();
                break;
            case 4:
                // Upload the filled template
                uploadFilledTemplate();
                break;
            default:
                handleNext();
        }
    };

    // Get button text
    const getButtonText = () => {
        if (activeStep === 4) return "Upload";
        if (activeStep === 2 && !hasDownloaded) return "Download";
        if (activeStep === 1 && !templateFile) return "Generate Template";
        return "Next";
    };

    // Determine which steps to show in stepper
    const getDisplaySteps = () => {
        if (hasExistingFile) {
            return ["Choose Method", "Upload File"];
        }
        return steps;
    };

    // Get current step index for stepper display
    const getStepperStep = () => {
        if (hasExistingFile) {
            if (activeStep === 0) return 0;
            if (activeStep === 4) return 1;
            return 1;
        }
        return activeStep;
    };

    return (
        <StyledDialog
            open={open}
            TransitionComponent={Transition}
            keepMounted
            onClose={handleCloseDialog}
            maxWidth="md"
            fullWidth
        >
            <StyledDialogTitle>
                <Typography variant="h6">Add New Property</Typography>
                <IconButton edge="end" color="inherit" onClick={handleCloseDialog} aria-label="close">
                    <CloseIcon/>
                </IconButton>
            </StyledDialogTitle>

            <DialogContent sx={{p: 3}}>
                {activeStep < 5 && activeStep !== 0 && (
                    <Stepper activeStep={getStepperStep()} sx={{mb: 4}}>
                        {getDisplaySteps().map((label) => (
                            <Step key={label}>
                                <StepLabel>{label}</StepLabel>
                            </Step>
                        ))}
                    </Stepper>
                )}

                {activeStep === 5 ? (
                    getStepContent(5)
                ) : (
                    getStepContent(activeStep)
                )}
            </DialogContent>

            {activeStep < 5 && activeStep !== 0 && (
                <DialogActions sx={{px: 3, py: 2, borderTop: '1px solid', borderColor: 'divider'}}>
                    <Button
                        onClick={() => {
                            if (hasExistingFile && activeStep === 4) {
                                // Go back to method selection
                                setActiveStep(0);
                                setHasExistingFile(false);
                                setPropertyFile(null);
                            } else {
                                handleBack();
                            }
                        }}
                        disabled={activeStep === 1}
                        sx={{borderRadius: 2}}
                    >
                        {hasExistingFile && activeStep === 4 ? 'Change Method' : 'Back'}
                    </Button>
                    <LoadingButton
                        variant="contained"
                        onClick={handleNextButtonClick}
                        disabled={!isNextButtonEnabled()}
                        loading={isLoading}
                        loadingPosition="end"
                        endIcon={<SendIcon/>}
                        sx={{borderRadius: 2}}
                    >
                        {getButtonText()}
                    </LoadingButton>
                </DialogActions>
            )}
        </StyledDialog>
    );
};